
import { useState, useEffect, useMemo } from 'react';
import { fetchFAQData, FAQCategory } from '../../../lib/wordpress';

// モックデータ（API取得失敗時のフォールバック）
const fallbackFAQs: FAQCategory[] = [
  {
    id: 1,
    name: '登録・費用について',
    items: [
      { id: 101, question: '登録や相談に費用はかかりますか?', answer: '一切かかりません。登録、相談、職場紹介まですべて無料です。無理な営業や、ご連絡も一切しませんのでご安心してご利用ください。', categoryId: 1 },
      { id: 102, question: '登録にはどのくらい時間がかかりますか?', answer: 'LINEでのご登録は最短5分で完了します。その後、担当者からご連絡させていただき、ご希望の条件をお伺いいたします。', categoryId: 1 },
      { id: 103, question: '登録後、すぐに働かなくてもいいですか?', answer: 'はい、もちろんです。まずはご登録だけでも大丈夫です。良い求人が出たタイミングでご紹介させていただきます。', categoryId: 1 },
    ],
  },
  {
    id: 2,
    name: '働き方について',
    items: [
      { id: 201, question: '週3日だけでも本当に働けますか?', answer: 'はい、可能です。週3日、1日6時間からの勤務も多数ご用意しています。実際に60代の女性の方でも、週3日の出勤で毎月16万円の収入に繋がっています。', categoryId: 2 },
      { id: 202, question: 'ブランクが5年以上ありますが、大丈夫でしょうか?', answer: 'もちろん大丈夫です。当社の登録スタッフの40%がブランクからの復帰です。事前面談や職場見学を通じて、安心して復帰できる職場をご案内いたします。', categoryId: 2 },
      { id: 203, question: '夜勤なしの求人はありますか?', answer: 'はい、日勤のみの求人も多数ございます。ご希望の勤務時間帯をお伝えいただければ、条件に合った求人をご紹介いたします。', categoryId: 2 },
      { id: 204, question: '扶養内で働きたいのですが可能ですか?', answer: 'はい、可能です。扶養内でのお仕事も多数ご用意しております。年収の上限に合わせた勤務プランをご提案いたします。', categoryId: 2 },
    ],
  },
  {
    id: 3,
    name: '派遣の仕組みについて',
    items: [
      { id: 301, question: '派遣で働くのが初めてです。どんな仕組みですか?', answer: '派遣の仕組みは、派遣元(トラスト)が雇用主となり、派遣先(施設)にあなたを派遣します。給与や福利厚生は派遣元が担当し、派遣先では施設の指示を受ける形です。', categoryId: 3 },
      { id: 302, question: '社会保険には加入できますか?', answer: 'はい、一定の条件を満たせば社会保険(健康保険・厚生年金)に加入いただけます。詳しくは担当者にお気軽にご相談ください。', categoryId: 3 },
      { id: 303, question: '交通費は支給されますか?', answer: 'はい、交通費は別途支給いたします。通勤手段やルートに応じて、実費を支給させていただきます。', categoryId: 3 },
    ],
  },
  {
    id: 4,
    name: 'サポートについて',
    items: [
      { id: 401, question: 'LINE での相談はどのくらいで返信がありますか?', answer: '平日9:00-18:00の間でしたら、通常1時間以内にお返事いたします。土日祝日も営業しておりますので、お気軽にお問合せください。', categoryId: 4 },
      { id: 402, question: '就業後のサポートはありますか?', answer: 'はい、就業後も担当者が定期的にフォローいたします。職場での悩みや困りごとがあれば、いつでもご相談ください。迅速に対応いたします。', categoryId: 4 },
      { id: 403, question: '職場が合わなかった場合はどうなりますか?', answer: 'まずは担当者にご相談ください。職場環境の改善を働きかけたり、別の職場をご紹介することも可能です。無理に続ける必要はありません。', categoryId: 4 },
    ],
  },
  {
    id: 5,
    name: '正社員について',
    items: [
      { id: 501, question: '正社員への転換は可能ですか?', answer: 'はい、派遣先での実績や評価に応じて、正社員登用の道もございます。キャリアアップを目指す方もぜひご相談ください。', categoryId: 5 },
    ],
  },
];

export default function FAQList() {
  const [faqData, setFaqData] = useState<FAQCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [openIndex, setOpenIndex] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loadFAQ = async () => {
      setIsLoading(true);
      try {
        const data = await fetchFAQData();
        if (!cancelled) {
          if (data.length > 0) {
            setFaqData(data);
          } else {
            setFaqData(fallbackFAQs);
          }
        }
      } catch {
        if (!cancelled) {
          setFaqData(fallbackFAQs);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    loadFAQ();
    return () => { cancelled = true; };
  }, []);

  // 検索フィルタリング
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return faqData;

    const query = searchQuery.trim().toLowerCase();
    return faqData
      .map((category) => ({
        ...category,
        items: category.items.filter(
          (item) =>
            item.question.toLowerCase().includes(query) ||
            item.answer.toLowerCase().includes(query)
        ),
      }))
      .filter((category) => category.items.length > 0);
  }, [faqData, searchQuery]);

  const totalResults = useMemo(
    () => filteredData.reduce((sum, cat) => sum + cat.items.length, 0),
    [filteredData]
  );

  const toggleFAQ = (key: string) => {
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <section className="py-12 sm:py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 検索フォーム */}
        <div className="mb-10 sm:mb-12">
          <div className="relative max-w-2xl mx-auto">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <div className="w-5 h-5 flex items-center justify-center text-gray-400">
                <i className="ri-search-line text-lg"></i>
              </div>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="キーワードで質問を検索..."
              className="w-full pl-12 pr-10 py-3.5 sm:py-4 text-sm sm:text-base bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all placeholder-gray-400"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-4 flex items-center cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors">
                  <i className="ri-close-circle-fill text-lg"></i>
                </div>
              </button>
            )}
          </div>
          {searchQuery.trim() && (
            <p className="text-center text-sm text-gray-500 mt-3">
              「{searchQuery.trim()}」の検索結果：<strong className="text-gray-700">{totalResults}件</strong>
            </p>
          )}
        </div>

        {/* ローディング */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-10 h-10 border-3 border-blue-200 border-t-blue-500 rounded-full animate-spin mb-4"></div>
            <p className="text-sm text-gray-500">質問を読み込んでいます...</p>
          </div>
        )}

        {/* 検索結果0件 */}
        {!isLoading && searchQuery.trim() && filteredData.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
              <i className="ri-question-line text-3xl text-gray-400"></i>
            </div>
            <p className="text-gray-700 text-base sm:text-lg font-medium mb-2">
              該当する質問が見つかりませんでした。
            </p>
            <p className="text-gray-500 text-sm sm:text-base mb-6">
              お気軽にお問い合わせください。
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <a
                href="https://lin.ee/MJ3LVM2"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 bg-[#06C755] hover:bg-[#05b34c] text-white px-5 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-line-fill text-sm"></i>
                </div>
                <span>LINEで相談する</span>
              </a>
              <a
                href="tel:052-887-6243"
                className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-5 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line text-sm"></i>
                </div>
                <span>052-887-6243</span>
              </a>
            </div>
          </div>
        )}

        {/* FAQリスト */}
        {!isLoading && filteredData.map((category, catIndex) => (
          <div key={category.id} className="mb-10 sm:mb-14 last:mb-0">
            <h4 className="text-xl sm:text-2xl font-bold text-gray-900 mb-5 sm:mb-6 flex items-center gap-3">
              <a href={`#category-${category.id}`} id={`category-${category.id}`} className="flex items-center gap-3">
                <span className="w-1.5 h-7 bg-blue-500 rounded-full inline-block"></span>
                {category.name}
              </a>
            </h4>
            <div className="space-y-3 sm:space-y-4">
              {category.items.map((faq, faqIndex) => {
                const key = `${catIndex}-${faqIndex}`;
                const isOpen = openIndex === key;
                return (
                  <div
                    key={faq.id}
                    className="bg-gray-50 rounded-2xl overflow-hidden transition-all duration-200"
                  >
                    <button
                      onClick={() => toggleFAQ(key)}
                      className="w-full text-left p-4 sm:p-6 flex items-center justify-between gap-4 cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <h3 className="text-base sm:text-lg font-bold text-gray-900">
                        Q. {faq.question}
                      </h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`ri-arrow-down-s-line text-xl text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}></i>
                      </div>
                    </button>
                    <div
                      className={`transition-all duration-200 ease-in-out overflow-hidden ${isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}
                    >
                      <div className="px-4 sm:px-6 pb-4 sm:pb-6 text-sm sm:text-base text-gray-700 leading-relaxed whitespace-pre-line">
                        A. {faq.answer}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
